/**
 * TK-38 việc 2d/2e (lee 23/10) — THÂN DIM: thanh 0–100 + Gửi + **Trả về lịch** (luật trả quyền: mọi lệnh tay có đường về chạy tự động
 *   tại chỗ). TK-46 §1: chỉ còn MỘT chỗ dùng — thân của khối "Điều khiển độ sáng" (`KhoiDieuKhien`), phạm vi Tuyến · Tủ · Nhóm · Đèn.
 *   · tuyến ⇒ `POST /api/nema/tuyen/:id/lenh` · tủ ⇒ `POST /api/nema/tu/:id/lenh` (addrs vắng = mọi đèn) · đèn ⇒ cùng đường, addrs [addr]
 *     · nhóm ⇒ đường của cấp (tủ hoặc tuyến) kèm `nhomTb` (số nhóm thiết bị — máy chủ chọn đúng đèn của nhóm).
 *   · Luôn qua `<KhoaTheoTuyen co="canControl">` (khoá-không-ẩn; nhận `qd` ba ca).
 *   · Mọi lệnh (tuyến · tủ · nhóm · đèn — Gửi lẫn Trả về lịch) hỏi xác nhận trước khi gửi (TK-47 §7).
 *   · TK-48 (bản v06): "Mức sáng đang ghi nhận" → "Muốn đặt" bằng SỐ LỚN; trùng nhau / chưa chọn ⇒ một số (`cachHienMuc`).
 *     Nút đặt nhanh Tắt · 25 · 50 · 75 · 100 dạng viên — CHỈ đổi giá trị, KHÔNG gửi. Chưa chọn ⇒ Gửi khoá.
 *   · TK-48 §3.2: chưa có mức nào (không mức báo, chưa chọn) ⇒ thanh ở trạng thái "chưa chọn" (không núm, không vệt) — KHÔNG nằm ở 50 %.
 *   · TK-48: "Gửi yêu cầu" / "Trả về lịch" nằm cuối thân, trong khung điều khiển (khung nổi / tấm trượt) — không còn dock cố định.
 *   · TK-44: `khoaVi` (lý do máy chủ tính) ⇒ thân THU còn MỘT dòng lý do (+ Trả về lịch nếu còn đi được); `khoaTraVe` khoá Trả về lịch.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { ConfirmDialog, useToast } from '../ui';
import { KhoaTheoTuyen } from '../Khoa';
import { api, errorMessage } from '../../lib/api';
import type { QuyetDinhCoTuyen } from '../../lib/quyen';
import { DAT_NHANH, cachHienMuc } from '../../lib/dieuKhienDoSang';

type Qd = QuyetDinhCoTuyen | 'khong-co-tuyen' | 'chua-biet';
export type DichDim =
  | { loai: 'tuyen'; id: string }
  | { loai: 'tu'; id: string }
  | { loai: 'den'; cabinetId: string; addr: number }
  /** TK-46: nhóm thiết bị `so` — trong MỘT tủ (`tuId`) hoặc cả tuyến (`tuyenId`) */
  | { loai: 'nhom'; so: number; tuId: string | null; tuyenId: string };
type Than = { kind: 'dim' | 'tra_ve_lich'; level?: number };

const duongLenh = (d: DichDim) => {
  if (d.loai === 'tuyen') return `/api/nema/tuyen/${d.id}/lenh`;
  if (d.loai === 'nhom') return d.tuId ? `/api/nema/tu/${d.tuId}/lenh` : `/api/nema/tuyen/${d.tuyenId}/lenh`;
  return `/api/nema/tu/${d.loai === 'tu' ? d.id : d.cabinetId}/lenh`;
};
const thanLenh = (d: DichDim, b: Than) => (d.loai === 'den' ? { ...b, addrs: [d.addr] } : d.loai === 'nhom' ? { ...b, nhomTb: d.so } : b);
const tenCap = (d: DichDim) => (d.loai === 'tuyen' ? 'cả tuyến' : d.loai === 'tu' ? 'cả tủ' : d.loai === 'nhom' ? `nhóm ${d.so}` : 'đèn');
const So = ({ v, o }: { v: number | null; o: string }) => (
  <strong className={o}>{v == null ? '—' : v}{v != null && <small>%</small>}</strong>
);

export function DimCap({ dich, ten, qd, mucDau = null, khoaVi = null, khoaTraVe = null, nhanDim, nhanTraVe }: {
  dich: DichDim; ten: string; qd: Qd;
  /** "Mức sáng đang ghi nhận" — mức thiết bị báo (đèn) / mức chung (nhiều đèn); null ⇒ "—" */
  mucDau?: number | null;
  /** khoá Dim — lý do máy chủ tính ⇒ thân thu còn một dòng */
  khoaVi?: string | null;
  /** khoá Trả về lịch — tủ mất kết nối / mesh tắt */
  khoaTraVe?: string | null;
  /** câu số nhận lệnh trong hộp xác nhận */
  nhanDim?: ReactNode; nhanTraVe?: ReactNode;
}) {
  const [muc, setMuc] = useState(mucDau ?? 0);
  /* người chưa chọn mức ⇒ thanh theo mức thật mới; "Muốn đặt" chưa có */
  const [daKeo, setDaKeo] = useState(false);
  useEffect(() => { if (!daKeo && mucDau != null) setMuc(mucDau); }, [mucDau, daKeo]);
  /* vừa gửi dim: giữ mức đã gửi + dòng "Đã gửi X %" tới khi đèn báo đúng mức (đèn) hoặc 60 s */
  const [daGui, setDaGui] = useState<number | null>(null);
  useEffect(() => { if (daGui !== null && mucDau === daGui) { setDaGui(null); setDaKeo(false); } }, [mucDau, daGui]);
  useEffect(() => { if (daGui === null) return; const t = window.setTimeout(() => setDaGui(null), 60_000); return () => window.clearTimeout(t); }, [daGui]);
  const [hoi, setHoi] = useState<Than | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const gui = useMutation({
    mutationFn: (b: Than) => api.post<unknown>(duongLenh(dich), thanLenh(dich, b)),
    onSuccess: (_r, b) => {
      setHoi(null);
      if (b.kind === 'dim') { setDaGui(b.level ?? null); setDaKeo(true); } else { setDaGui(null); setDaKeo(false); }
      toast.success(b.kind === 'dim' ? `Dim ${ten} ${b.level} % — đã gửi` : `${ten} — trả về lịch`);
      void qc.invalidateQueries({ queryKey: ['nema'] });
    },
    onError: (e) => { setHoi(null); toast.error(`${ten} — không gửi được`, errorMessage(e)); },
  });
  const tatChung = qd !== 'cho-qua' || gui.isPending;
  const tatTraVe = tatChung || !!khoaTraVe;
  /* TK-47: "Muốn đặt" chưa chọn ⇒ Gửi khoá (kể cả khi đã biết mức đang ghi nhận) */
  const chuaChon = !daKeo;
  /* TK-48 §3.2: không mức báo + chưa chọn ⇒ thanh CHƯA CHỌN (không núm); chạm vào thanh mới đặt mức */
  const thanhTrong = chuaChon && mucDau == null;
  const canHoi = true;   // TK-47: cả một đèn cũng hỏi trước khi gửi
  const bam = (b: Than) => (canHoi ? setHoi(b) : gui.mutate(b));
  /* nút đặt nhanh: CHỈ đổi giá trị "Muốn đặt" — không gọi API */
  const datNhanh = (m: number) => { setMuc(m); setDaKeo(true); };
  const cap = tenCap(dich);
  const hien = cachHienMuc(mucDau, chuaChon ? null : muc);
  const nutTraVe = (
    <button type="button" className="op-nut-phu" disabled={tatTraVe} onClick={() => bam({ kind: 'tra_ve_lich' })} data-o="tra-ve-lich">Trả về lịch</button>
  );
  return (
    <KhoaTheoTuyen qd={qd} co="canControl" hienHuyHieu={false} trungTinh className="w-full">
      {khoaVi ? (
        /* TK-46 §1: không đèn nào nhận dim ⇒ MỘT dòng lý do; trả về lịch còn đi được thì giữ nút ấy */
        <div className="flex flex-col gap-2" data-o={`dim-${dich.loai}`} data-khoa="1">
          <span className="text-sm text-chu-3" data-o="dim-khoa-vi">{khoaVi}</span>
          {!khoaTraVe && <div className="op-hanh-dong" data-o="dock-dim">{nutTraVe}</div>}
        </div>
      ) : (
        <div className="op-than-dim" data-o={`dim-${dich.loai}`}>
          {hien === 'mot' ? (
            <div className="op-muc-mot" data-o="dang-bao-muon-dat">
              <span>Mức sáng đang ghi nhận</span>
              <span data-o="dang-bao"><So v={mucDau} o={mucDau == null ? 'op-so op-so-chua' : 'op-so'} /></span>
              <span hidden data-o="muon-dat">{chuaChon ? '—' : `${muc} %`}</span>
            </div>
          ) : (
            <div className="op-muc-hai" data-o="dang-bao-muon-dat">
              <div><span>Mức sáng đang ghi nhận</span><span data-o="dang-bao"><So v={mucDau} o={mucDau == null ? 'op-so op-so-chua' : 'op-so'} /></span></div>
              <ArrowRight className="h-4 w-4 shrink-0 text-chu-3" aria-hidden="true" />
              <div><span>Muốn đặt</span><span data-o="muon-dat"><So v={muc} o="op-so op-so-dat" /></span></div>
            </div>
          )}
          <input type="range" min={0} max={100} step={5} value={thanhTrong ? 0 : muc} disabled={tatChung}
            onChange={(e) => { setMuc(Number(e.target.value)); setDaKeo(true); }}
            onPointerUp={(e) => { if (!daKeo) { setMuc(Number(e.currentTarget.value)); setDaKeo(true); } }}
            aria-label={`Dim ${ten}`} aria-valuetext={thanhTrong ? 'chưa chọn' : `${muc} %`}
            className="op-thanh" data-chua-chon={thanhTrong ? '1' : undefined} data-o="thanh-dim"
            style={{ '--pct': `${thanhTrong ? 0 : muc}%` } as React.CSSProperties} />
          <div className="op-dat-nhanh" data-o="dat-nhanh">
            {DAT_NHANH.map((m) => (
              <button key={m} type="button" disabled={tatChung} onClick={() => datNhanh(m)} aria-pressed={daKeo && muc === m} data-muc={m}>
                {m === 0 ? 'Tắt' : `${m}%`}
              </button>
            ))}
          </div>
          {khoaTraVe && <span className="text-xs text-chu-3" data-o="dim-khoa-tra-ve">{khoaTraVe}</span>}
          {daGui !== null && <span className="text-xs text-chu-2" data-o="da-gui-dim">Đã gửi {daGui} %{dich.loai === 'den' ? ' · chờ đèn báo' : ''}</span>}
          <div className="op-hanh-dong" data-o="dock-dim">
            <button type="button" className="op-nut-gui" disabled={tatChung || chuaChon} onClick={() => bam({ kind: 'dim', level: muc })} data-o="gui-dim">Gửi yêu cầu</button>
            {nutTraVe}
          </div>
        </div>
      )}
      {canHoi && (
        <ConfirmDialog open={hoi !== null} danger={false} loading={gui.isPending}
          title={hoi?.kind === 'dim' ? `Dim ${cap} ${hoi.level} %?` : `Trả về lịch ${cap}?`}
          message={<>{ten}{(hoi?.kind === 'dim' ? nhanDim : nhanTraVe) ? <span className="text-chu-2" data-o="hoi-so-nhan"> · {hoi?.kind === 'dim' ? nhanDim : nhanTraVe}</span> : null}</>}
          confirmText={hoi?.kind === 'dim' ? 'Gửi' : 'Trả về lịch'}
          onConfirm={() => hoi && gui.mutate(hoi)} onCancel={() => setHoi(null)} />
      )}
    </KhoaTheoTuyen>
  );
}
